import { useCallback, useMemo, useState } from "react";

import { ReservationRequestStatus } from "@/lib/gql/__generated__/graphql";

import type { DateRange } from "@/components/ui/calendar/types/calendar-range";

import type * as Model from "../../../../model";

type ReservationType = Model.Constants.ReservationTypeForSearchReservationRequestList;
/**
 * 日付での検索
 */
type DateRangeCase = {
	reservationType: Exclude<ReservationType, "monthCount">;
	from?: string;
	to?: string;
	// リクエスト予約ステータス
	reservationRequestStatus: ReservationRequestStatus;
};
/**
 * 直近3ヶ月の新着リクエストでの検索
 */
type MonthCountCase = {
	reservationType: Extract<ReservationType, "monthCount">;
	// 直近⚪︎ヶ月
	months: number;
	// リクエスト予約ステータス
	reservationRequestStatus: ReservationRequestStatus;
};

type SearchState = DateRangeCase | MonthCountCase;

export const useHandleSearchActions = (
	// 予約の検索
	onSearchReservationList: (props: Model.UseCase.SearchReservationRequestListProps) => void,
) => {
	const [searchState, setSearchState] = useState<SearchState>({
		reservationType: "monthCount",
		months: 3,
		reservationRequestStatus: ReservationRequestStatus.Unnoticed,
	});
	/**
	 * 予約検索タイプの変更
	 */
	const handleChangeReservationType = useCallback((value: string) => {
		const reservationType = value as ReservationType;
		if (reservationType === "monthCount") {
			setSearchState((prev) => ({
				...prev,
				reservationType,
				months: 3,
				reservationRequestStatus: ReservationRequestStatus.Unnoticed,
			}));
		} else {
			setSearchState((prev) => ({
				...prev,
				reservationType,
				reservationRequestStatus: ReservationRequestStatus.Unnoticed,
			}));
		}
	}, []);

	/**
	 * リクエスト予約ステータスの変更
	 */
	const handleChangeReservationRequestStatus = useCallback(
		(value: string) => {
			if (searchState.reservationType === "monthCount") return;

			const reservationRequestStatus = value as ReservationRequestStatus;
			setSearchState((prev) => ({
				...prev,
				reservationRequestStatus,
			}));
		},
		[searchState.reservationType],
	);

	/**
	 * 日付の変更
	 */
	const handleChangeFromAndTo = useCallback(({ from, to }: DateRange) => {
		setSearchState((prev) => ({ ...prev, from: from, to: to }));
	}, []);

	/**
	 * 日付での検索
	 */
	const onSearchReservationListByRangeDate = useCallback(
		(
			reservationType: Exclude<ReservationType, "monthCount">,
			from: string,
			to: string,
			reservationRequestStatus: ReservationRequestStatus,
		) => {
			onSearchReservationList({ type: reservationType, from, to, reservationRequestStatus });
		},
		[onSearchReservationList],
	);
	/**
	 * 直近3ヶ月の新着リクエストでの検索
	 */
	const onSearchReservationListByMonthCount = useCallback(
		(
			reservationType: Extract<ReservationType, "monthCount">,
			months: number,
			reservationRequestStatus: ReservationRequestStatus,
		) => {
			onSearchReservationList({ type: reservationType, months, reservationRequestStatus });
		},
		[onSearchReservationList],
	);
	/**
	 * 予約の検索
	 */
	const handleClickSearchReservationList = useCallback(() => {
		if (searchState.reservationType === "monthCount") {
			onSearchReservationListByMonthCount(
				searchState.reservationType,
				searchState.months,
				searchState.reservationRequestStatus,
			);
		} else {
			if (!searchState.from) return;

			const from = searchState.from;
			/**
			 * 日付の範囲が指定されていない場合は、開始日と終了日を同じにする
			 */
			const to = searchState.to ?? from;
			onSearchReservationListByRangeDate(
				searchState.reservationType,
				from,
				to,
				searchState.reservationRequestStatus,
			);
		}
	}, [onSearchReservationListByMonthCount, onSearchReservationListByRangeDate, searchState]);

	const disabled = useMemo(() => {
		if (searchState.reservationType === "monthCount") {
			return false;
		}

		return !(searchState.from && searchState.to);
	}, [searchState]);

	return {
		//
		searchState,
		disabled,
		//
		// 予約タイプの変更
		handleChangeReservationType,
		// リクエスト予約ステータスの変更
		handleChangeReservationRequestStatus,
		// 日付の変更
		handleChangeFromAndTo,
		// 予約の検索
		handleClickSearchReservationList,
	};
};
